import { ResponsivePie } from '@nivo/pie'
import type React from 'react'
import type { DashboardTradeTypeSlice } from '../../../redux/services/coreApi'
import { formatMoneyCompact } from './dashboardFormat'

interface TradeTypeDonutProps {
  data: DashboardTradeTypeSlice[]
  /** Display currency for the per-slice volume (e.g. "DKK", "EUR"). */
  currency: string
}

const SLICE_COLORS: Record<string, string> = {
  on_ramp: '#46AF77',
  off_ramp: '#AC5647',
}

// Buy vs sell split over the trailing window. The donut centre carries the headline (total trades)
// while a legend to its right spells out each ramp's share, count and volume — the card is wide and
// short, so a lone donut would float in empty space and the centre text would crowd the ring.
const TradeTypeDonut: React.FC<TradeTypeDonutProps> = ({ data, currency }) => {
  const totalOrders = data.reduce((sum, s) => sum + s.orderCount, 0)

  const slices = data.map(slice => ({
    ...slice,
    color: SLICE_COLORS[slice.key] ?? '#677298',
    pct: totalOrders ? Math.round((slice.orderCount / totalOrders) * 100) : 0,
  }))

  const pieData = slices.map(slice => ({
    id: slice.key,
    label: slice.label,
    value: slice.orderCount,
    color: slice.color,
  }))

  return (
    <div className="flex h-full items-center gap-4 xl:gap-6">
      {/* Donut — pinned square so it never squashes in the wide card */}
      <div className="relative aspect-square h-full max-h-full shrink-0">
        <ResponsivePie
          data={pieData}
          margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
          innerRadius={0.72}
          padAngle={1.5}
          cornerRadius={4}
          colors={{ datum: 'data.color' }}
          borderWidth={0}
          enableArcLinkLabels={false}
          enableArcLabels={false}
          isInteractive={false}
          theme={{ fontSize: 14 }}
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-3xl font-bold leading-none tabular-nums text-white xl:text-4xl">
            {totalOrders}
          </span>
        </div>
      </div>

      {/* Legend — fills the width that the bare donut used to leave empty */}
      <ul className="flex min-w-0 flex-1 flex-col justify-center gap-3">
        {slices.map(slice => (
          <li key={slice.key}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="flex items-center gap-2 truncate text-sm font-medium text-white/80">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: slice.color }}
                />
                {slice.label}
              </span>
              <span className="shrink-0 text-sm font-bold tabular-nums text-white">
                {slice.pct}%
              </span>
            </div>
            <div className="ml-[18px] mt-0.5 text-xs tabular-nums text-white/40">
              {slice.orderCount.toLocaleString('en-US')} trades ·{' '}
              {formatMoneyCompact(slice.volume, currency)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default TradeTypeDonut
